import type { ConfirmHandoverRequest, DispatchRecordItemDto } from "../services/dispatchApi";

type HandoverItem = Pick<DispatchRecordItemDto,
  "id" | "unitName" | "unit" | "bedAllocationRequiredQuantity" | "handoverBedOptions">;
type BedQuantities = Record<string, number>;

const lineUnit = (item: HandoverItem) => (item.unitName ?? item.unit ?? "").trim().toLowerCase();
export const isPieceHandover = (item: HandoverItem) => lineUnit(item) === "pieces";
export const handoverUnitLabel = (item: HandoverItem) => isPieceHandover(item) ? "pieces" : "trays";

export const isHandoverBedLine = (item: HandoverItem) =>
  lineUnit(item) === "trays" || (isPieceHandover(item) &&
    (item.bedAllocationRequiredQuantity > 0 || item.handoverBedOptions.length > 0));

// Preserve the legacy fallback when recorded beds cannot cover the line.
// Both backend fields already use commercial line units, including pieces from trays.
export const isBedSelectionRequired = (item: HandoverItem) =>
  item.bedAllocationRequiredQuantity > 0 &&
  item.handoverBedOptions.reduce((sum, bed) => sum + bed.availableTrays, 0) >= item.bedAllocationRequiredQuantity;

export const clampHandoverQuantity = (value: string, available: number, item: HandoverItem) => {
  const parsed = Number(value);
  const quantity = Number.isFinite(parsed) ? Math.max(0, Math.min(available, parsed)) : 0;
  return isPieceHandover(item) ? Math.floor(quantity) : quantity;
};

export const selectedBedQuantity = (item: HandoverItem, quantities: BedQuantities) =>
  item.handoverBedOptions.reduce((sum, bed) =>
    sum + (quantities[`${item.id}:${bed.greenhouseStockEntryBedId}`] ?? 0), 0);

export const hasInvalidBedAllocations = (items: HandoverItem[], quantities: BedQuantities) =>
  items.filter(isHandoverBedLine).some((item) => isBedSelectionRequired(item) && (
    selectedBedQuantity(item, quantities) !== item.bedAllocationRequiredQuantity ||
    item.handoverBedOptions.some((bed) => {
      const quantity = quantities[`${item.id}:${bed.greenhouseStockEntryBedId}`] ?? 0;
      return !Number.isFinite(quantity) || quantity < 0 || quantity > bed.availableTrays ||
        (isPieceHandover(item) && !Number.isSafeInteger(quantity));
    })
  ));

export const buildHandoverItemAllocations = (
  items: HandoverItem[], quantities: BedQuantities,
): ConfirmHandoverRequest["itemAllocations"] => items.filter(isHandoverBedLine).map((item) => ({
  dispatchRecordItemId: item.id,
  bedAllocations: (isBedSelectionRequired(item) ? item.handoverBedOptions : [])
    .map((bed) => ({
      greenhouseStockEntryBedId: bed.greenhouseStockEntryBedId,
      // Legacy property name: the value is already in the line's commercial units.
      trayQuantity: quantities[`${item.id}:${bed.greenhouseStockEntryBedId}`] ?? 0,
    }))
    .filter((allocation) => allocation.trayQuantity > 0),
}));
